import React, { useState } from 'react';
import { clsx } from 'clsx';
import { DynamicFormField, DynamicFieldType, DynamicFieldOption, DynamicFieldValidation } from './DynamicField';

export interface DynamicFormFieldConfig {
  id: string;
  key: string;
  name: string;
  fieldType: DynamicFieldType;
  description?: string;
  options?: DynamicFieldOption[];
  defaultValue?: any;
  validation?: DynamicFieldValidation;
  placeholder?: string;
  helpText?: string;
  isRequired?: boolean;
  isVisible?: boolean;
  sortOrder?: number;
}

export interface DynamicFormSectionProps {
  id: string;
  sectionKey: string;
  name: string;
  description?: string;
  icon?: string;
  displayLocation?: 'main' | 'sidebar' | 'modal';
  isCollapsible?: boolean;
  isCollapsedByDefault?: boolean;
  isRequired?: boolean;
  fields: DynamicFormFieldConfig[];
  values: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
  columns?: 1 | 2;
  className?: string;
}

export function DynamicFormSection({
  id,
  sectionKey,
  name,
  description,
  icon,
  isCollapsible = false,
  isCollapsedByDefault = false,
  fields,
  values,
  errors = {},
  onChange,
  disabled = false,
  columns = 1,
  className,
}: DynamicFormSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedByDefault);

  // Filter out non-visible fields and sort by sortOrder
  const visibleFields = fields
    .filter((field) => field.isVisible !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  if (visibleFields.length === 0) {
    return null;
  }

  const toggleCollapse = () => {
    if (isCollapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div
      className={clsx(
        'border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden',
        className
      )}
    >
      {/* Section Header */}
      <div
        className={clsx(
          'px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700',
          isCollapsible && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-750'
        )}
        onClick={toggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-gray-500 dark:text-gray-400">{icon}</span>
            )}
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {name}
            </h3>
          </div>
          {isCollapsible && (
            <button
              type="button"
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-expanded={!isCollapsed}
            >
              <svg
                className={clsx(
                  'h-5 w-5 transition-transform',
                  isCollapsed ? '' : 'rotate-180'
                )}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
        {description && !isCollapsed && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>

      {/* Section Fields */}
      {!isCollapsed && (
        <div className="p-4">
          <div
            className={clsx(
              'grid gap-4',
              columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
            )}
          >
            {visibleFields.map((field) => (
              <DynamicFormField
                key={field.id}
                id={`${sectionKey}-${field.key}`}
                fieldKey={field.key}
                name={field.name}
                fieldType={field.fieldType}
                value={values[field.key] ?? field.defaultValue ?? ''}
                onChange={(value) => onChange(field.key, value)}
                options={field.options}
                validation={field.validation}
                placeholder={field.placeholder}
                helpText={field.helpText}
                isRequired={field.isRequired}
                error={errors[field.key]}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Complete dynamic form with multiple sections
export interface DynamicFormSectionConfig {
  id: string;
  key: string;
  name: string;
  description?: string;
  icon?: string;
  displayLocation?: 'main' | 'sidebar' | 'modal';
  isCollapsible?: boolean;
  isCollapsedByDefault?: boolean;
  isRequired?: boolean;
  sortOrder?: number;
  fields: DynamicFormFieldConfig[];
}

export interface DynamicFormProps {
  sections: DynamicFormSectionConfig[];
  values: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
  displayLocation?: 'main' | 'sidebar' | 'modal';
  columns?: 1 | 2;
  className?: string;
}

export function DynamicForm({
  sections,
  values,
  errors = {},
  onChange,
  disabled = false,
  displayLocation = 'main',
  columns = 1,
  className,
}: DynamicFormProps) {
  // Filter sections by display location and sort by sortOrder
  const filteredSections = sections
    .filter((section) => !section.displayLocation || section.displayLocation === displayLocation)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  if (filteredSections.length === 0) {
    return null;
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {filteredSections.map((section) => (
        <DynamicFormSection
          key={section.id}
          id={section.id}
          sectionKey={section.key}
          name={section.name}
          description={section.description}
          icon={section.icon}
          isCollapsible={section.isCollapsible}
          isCollapsedByDefault={section.isCollapsedByDefault}
          isRequired={section.isRequired}
          fields={section.fields}
          values={values}
          errors={errors}
          onChange={onChange}
          disabled={disabled}
          columns={columns}
        />
      ))}
    </div>
  );
}

// Helper to convert API schema to form config
export function schemaToFormConfig(
  schema: {
    fieldGroups: Array<{
      id: string;
      key: string;
      name: string;
      description?: string;
      icon?: string;
      displayLocation?: 'main' | 'sidebar' | 'modal';
      isCollapsible?: boolean;
      isCollapsedByDefault?: boolean;
      isRequired?: boolean;
      sortOrder?: number;
      fields: Array<{
        id: string;
        key: string;
        name: string;
        fieldType: string;
        description?: string;
        options?: Array<{ value: string; label: string; color?: string }>;
        defaultValue?: any;
        validation?: Record<string, any>;
        placeholder?: string;
        helpText?: string;
        isRequired?: boolean;
        isVisible?: boolean;
        sortOrder?: number;
      }>;
    }>;
  }
): DynamicFormSectionConfig[] {
  return schema.fieldGroups.map((group) => ({
    id: group.id,
    key: group.key,
    name: group.name,
    description: group.description,
    icon: group.icon,
    displayLocation: group.displayLocation,
    isCollapsible: group.isCollapsible,
    isCollapsedByDefault: group.isCollapsedByDefault,
    isRequired: group.isRequired,
    sortOrder: group.sortOrder,
    fields: group.fields.map((field) => ({
      id: field.id,
      key: field.key,
      name: field.name,
      fieldType: field.fieldType as DynamicFieldType,
      description: field.description,
      options: field.options,
      defaultValue: field.defaultValue,
      validation: field.validation,
      placeholder: field.placeholder,
      helpText: field.helpText,
      isRequired: field.isRequired,
      isVisible: field.isVisible,
      sortOrder: field.sortOrder,
    })),
  }));
}
